import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import CreateDonorModal from './CreateDonorModal';
import EditDonorModal from './EditDonorModal';
import ViewDonorModal from './ViewDonorModal';
import UserAvatars from '../../../components/UserAvatars';
import ConfirmModal from '../../../components/ConfirmModal';
import HtmlContent from '../../../components/HtmlContent';
import { Link } from 'react-router-dom';

const Donors = () => {
  const { currentLang, fetchWithAuth, API_URL, getCsrfToken, user } = useAuth();
  
  // Состояние для данных
  const [donors, setDonors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Состояние для пагинации
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDonors, setTotalDonors] = useState(0);
  const [limit] = useState(50); // 50 доноров на страницу
  
  // Состояние для фильтров
  const [filters, setFilters] = useState({
    url: '', // URL донора
    title: '', // название донора
    tags: '', // теги
    date_from: '', // дата создания с
    date_to: '', // дата создания по
  });
  
  // Состояние для сортировки
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Состояние для модальных окон
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedDonorId, setSelectedDonorId] = useState(null);
  
  // Состояние для индикаторов загрузки
  const [deletingDonors, setDeletingDonors] = useState(new Set());
  
  // Состояние для модалки удаления
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [donorToDelete, setDonorToDelete] = useState(null);
  
  // Состояние для раскрытия фильтров
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  
  // Загрузка данных
  const fetchDonors = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }
      
      // Формируем параметры запроса
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: limit.toString(),
        sort_field: sortField,
        sort_direction: sortDirection,
      });
      
      // Добавляем фильтры, если они заполнены
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          const stringValue = typeof value === 'string' ? value.trim() : String(value);
          if (stringValue !== '') {
            params.append(key, stringValue);
          }
        }
      });
      
      const url = `${API_URL}${currentLang}/seo-donors?${params.toString()}`;
      console.log('API Request URL:', url);
      
      const response = await fetchWithAuth(url, {
        headers: {
          'X-CSRF-Token': currentCsrfToken,
        },
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setDonors(data.data?.donors || []);
        setTotalPages(data.data?.pages || 1);
        setTotalDonors(data.data?.total || 0);
      } else {
        throw new Error(data.message || 'Ошибка загрузки доноров');
      }
    } catch (error) {
      console.error('Ошибка загрузки доноров:', error);
      if (error.message === 'Требуется авторизация') {
        setError('Сессия истекла. Пожалуйста, войдите снова.');
      } else {
        setError(error.message);
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Загружаем данные при изменении параметров
  useEffect(() => {
    fetchDonors();
  }, [currentPage, sortField, sortDirection, filters]);
  
  // Автоматически раскрываем фильтры при наличии активных фильтров
  useEffect(() => {
    if (getActiveFiltersCount() > 0 && !filtersExpanded) {
      setFiltersExpanded(true);
    }
  }, [filters]);
  
  // Обработчики фильтров
  const handleFilterChange = (field, value) => {
    console.log(`Filter changed: ${field} = ${value}`);
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setCurrentPage(1); // Сбрасываем на первую страницу при изменении фильтров
  };
  
  // Обработчик сортировки
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };
  
  // Подсчет активных фильтров
  const getActiveFiltersCount = () => {
    return Object.values(filters).filter(value => {
      if (value === null || value === undefined || value === '') return false;
      if (typeof value === 'string') return value.trim() !== '';
      return true; // Для числовых значений
    }).length;
  };

  // Очистка фильтров
  const clearFilters = () => {
    setFilters({
      url: '',
      title: '',
      tags: '',
      date_from: '',
      date_to: '',
    });
    setCurrentPage(1);
  };
  
  // Форматирование даты
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('ru-RU');
    } catch (error) {
      return dateString;
    }
  };
  
  // Обрезка длинного URL
  const truncateUrl = (url, maxLength = 50) => {
    if (!url) return '-';
    if (url.length <= maxLength) return url;
    return url.substring(0, maxLength) + '...';
  };
  
  // Получение иконки сортировки
  const getSortIcon = (field) => {
    if (sortField !== field) return '↕️';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  // Функции для действий с донорами
  const handleViewDonor = (donorId) => {
    setSelectedDonorId(donorId);
    setViewModalOpen(true);
  };

  const handleEditDonor = (donorId) => {
    // Проверяем, что пользователь имеет доступ к донору
    const donor = donors.find(d => d.id === donorId);
    if (!donor) {
      alert(currentLang === 'ru' ? 'Донор не найден' : 'Donor not found');
      return;
    }

    // Проверяем, есть ли у пользователя доступ (все пользователи в access_users могут редактировать)
    const hasAccess = donor.access_users && donor.access_users.some(u => u.id === user?.id);

    if (!hasAccess) {
      showNotification(currentLang === 'ru' ? 'У вас нет доступа к редактированию этого донора' : 'You do not have access to edit this donor', 'error');
      return;
    }

    setSelectedDonorId(donorId);
    setEditModalOpen(true);
  };

  // Показать модалку подтверждения удаления
  const showDeleteConfirm = (donorId) => {
    // Находим донора в списке для проверки владельца
    const donor = donors.find(d => d.id === donorId);
    if (!donor) {
      showNotification(currentLang === 'ru' ? 'Донор не найден' : 'Donor not found', 'error');
      return;
    }

    // Проверяем, что пользователь является владельцем донора
    const isOwner = donor.access_users && donor.access_users.some(u => u.id === user?.id && u.type === 'owner');
    if (!isOwner) {
      showNotification(currentLang === 'ru' ? 'Только владелец может удалять донора' : 'Only the owner can delete the donor', 'error');
      return;
    }

    setDonorToDelete(donorId);
    setShowDeleteModal(true);
  };

  // Подтверждение удаления донора
  const handleDeleteConfirm = async () => {
    if (!donorToDelete) return;

    setDeletingDonors(prev => new Set(prev).add(donorToDelete));

    try {
      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }

      const response = await fetchWithAuth(`${API_URL}${currentLang}/seo-donors/${donorToDelete}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': currentCsrfToken,
        },
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Обновляем данные на странице
        fetchDonors();
        showNotification(currentLang === 'ru' ? 'Донор удален!' : 'Donor deleted!', 'success');
      } else {
        throw new Error(data.message || 'Ошибка удаления донора');
      }
    } catch (error) {
      console.error('Ошибка удаления донора:', error);
      showNotification(error.message, 'error');
    } finally {
      setDeletingDonors(prev => {
        const newSet = new Set(prev);
        newSet.delete(donorToDelete);
        return newSet;
      });
      setDonorToDelete(null);
    }
  };

  // Закрыть модалку удаления
  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDonorToDelete(null);
  };

  const handleEditSuccess = () => {
    // Обновляем данные на странице после успешного редактирования
    fetchDonors();
    // Уведомление уже показывается в модальном окне
  };

  // Функция для оптимистичного обновления донора в списке
  const updateDonorInList = (donorId, updatedData) => {
    console.log('Updating donor in list:', donorId, updatedData);
    setDonors(prevDonors => 
      prevDonors.map(donor => 
        donor.id === donorId 
          ? { ...donor, ...updatedData }
          : donor
      )
    );
  };

  // Функция для обновления доступа к донору
  const handleAccessChange = (donorId) => {
    // Перезагружаем данные конкретного донора
    const currentCsrfToken = getCsrfToken();
    if (currentCsrfToken) {
      fetchWithAuth(`${API_URL}${currentLang}/seo-donors/${donorId}`)
      .then(response => response.json())
      .then(data => {
        if (data.success && data.data) {
          updateDonorInList(donorId, data.data);
        }
      })
      .catch(error => {
        console.error('Ошибка обновления данных донора:', error);
      });
    }
  };

  const handleCreateSuccess = () => {
    // Обновляем данные на странице после успешного создания
    fetchDonors();
    showNotification(currentLang === 'ru' ? 'Донор успешно создан!' : 'Donor created successfully!', 'success');
  };

  // Функция показа уведомлений
  const showNotification = (message, type = 'success') => {
    const colors = {
      success: 'bg-green-500',
      error: 'bg-red-500',
      info: 'bg-blue-500',
      warning: 'bg-yellow-500'
    };
    
    const notification = document.createElement('div');
    notification.className = `fixed top-4 right-4 ${colors[type]} text-white px-6 py-3 rounded-lg shadow-lg z-50 transform transition-all duration-300 translate-x-full`;
    notification.textContent = message;
    document.body.appendChild(notification);
    
    // Анимация появления
    setTimeout(() => {
      notification.classList.remove('translate-x-full');
    }, 100);
    
    // Автоматическое скрытие через 3 секунды
    setTimeout(() => {
      notification.classList.add('translate-x-full');
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 300);
    }, 3000);
  };

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-6 flex-wrap">
        <div className="flex items-center space-x-3">
          <Link
            to="/seo/links"
            className="px-4 py-2 border border-purple-500 text-purple-500 rounded-lg flex items-center space-x-2"
          >
            <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 24 24" className="h-6 w-6" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><path fill="none" d="M0 0h24v24H0z"></path><path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"></path></svg>
            <span>{currentLang === 'ru' ? 'SEO Ссылки' : 'SEO Links'}</span>
          </Link>
          <div className="px-4 py-2 bg-purple-500 text-white rounded-lg flex items-center space-x-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <span>{currentLang === 'ru' ? 'SEO Доноры' : 'SEO Donors'}</span>
          </div>
          {getActiveFiltersCount() > 0 && (
            <span className="px-2 py-1 text-xs font-bold bg-brand-500 text-white rounded-full">
              {getActiveFiltersCount()} {currentLang === 'ru' ? 'фильтр' : 'filter'}
              {getActiveFiltersCount() > 1 && (currentLang === 'ru' ? 'а' : 's')}
            </span>
          )}
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setCreateModalOpen(true)}
            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>{currentLang === 'ru' ? 'Добавить донора' : 'Add Donor'}</span>
          </button>
          <button
            onClick={fetchDonors}
            disabled={loading}
            className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 disabled:opacity-50"
          >
            {loading ? (currentLang === 'ru' ? 'Загрузка...' : 'Loading...') : (currentLang === 'ru' ? 'Обновить' : 'Refresh')}
          </button>
        </div>
      </div>

      {/* Фильтры */}
      <div className="bg-white rounded-lg shadow-md mb-6 dark:bg-navy-700 dark:text-white dark:border-white/10">
        {/* Заголовок фильтров */}
        <div 
          className="p-4 cursor-pointer hover:bg-gray-50 border-b border-gray-200 dark:bg-navy-700 dark:text-white dark:border-white/10"
          onClick={() => setFiltersExpanded(!filtersExpanded)}
        >
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <h3 className="text-lg font-semibold">{currentLang === 'ru' ? 'Фильтры' : 'Filters'}</h3>
              {getActiveFiltersCount() > 0 && (
                <span className="px-2 py-1 text-xs font-bold bg-brand-500 text-white rounded-full">
                  {getActiveFiltersCount()}
                </span>
              )}
            </div>
            <div className="flex items-center space-x-2">
              {getActiveFiltersCount() > 0 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    clearFilters();
                  }}
                  className="px-3 py-1 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md"
                >
                  {currentLang === 'ru' ? 'Очистить' : 'Clear'}
                </button>
              )}
              <svg 
                className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${filtersExpanded ? 'rotate-180' : ''}`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
          
          {/* Быстрые фильтры */}
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                clearFilters();
              }}
              className={`px-3 py-1 text-xs rounded-full border ${
                getActiveFiltersCount() === 0
                  ? 'bg-brand-500 text-white border-brand-500' 
                  : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
              }`}
            >
              {currentLang === 'ru' ? 'Все доноры' : 'All donors'}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                const today = new Date().toISOString().split('T')[0];
                const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
                handleFilterChange('date_from', weekAgo);
                handleFilterChange('date_to', today);
              }}
              className="px-3 py-1 text-xs rounded-full border bg-blue-100 text-blue-700 border-blue-300 hover:bg-blue-200"
            >
              {currentLang === 'ru' ? 'За неделю' : 'Last week'}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                const today = new Date().toISOString().split('T')[0];
                const monthAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
                handleFilterChange('date_from', monthAgo);
                handleFilterChange('date_to', today);
              }}
              className="px-3 py-1 text-xs rounded-full border bg-green-100 text-green-700 border-green-300 hover:bg-green-200"
            >
              {currentLang === 'ru' ? 'За месяц' : 'Last month'}
            </button>
          </div>
        </div>

        {/* Раскрывающиеся фильтры */}
        {filtersExpanded && (
          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {/* URL донора */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {currentLang === 'ru' ? 'URL донора' : 'Donor URL'}
                </label>
                <input
                  type="text"
                  value={filters.url}
                  onChange={(e) => handleFilterChange('url', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
                  placeholder={currentLang === 'ru' ? 'Введите URL донора' : 'Enter donor URL'}
                />
              </div>
              
              {/* Название донора */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {currentLang === 'ru' ? 'Название' : 'Title'}
                </label>
                <input
                  type="text"
                  value={filters.title}
                  onChange={(e) => handleFilterChange('title', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
                  placeholder={currentLang === 'ru' ? 'Введите название' : 'Enter title'}
                />
              </div>
              
              {/* Теги */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {currentLang === 'ru' ? 'Теги' : 'Tags'}
                </label>
                <input
                  type="text"
                  value={filters.tags}
                  onChange={(e) => handleFilterChange('tags', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
                  placeholder={currentLang === 'ru' ? 'Введите теги' : 'Enter tags'}
                />
              </div>
              
              {/* Дата создания с */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {currentLang === 'ru' ? 'Дата создания с' : 'Created from'}
                </label>
                <input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => handleFilterChange('date_from', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
                />
              </div>
              
              {/* Дата создания по */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {currentLang === 'ru' ? 'Дата создания по' : 'Created to'}
                </label>
                <input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => handleFilterChange('date_to', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Статистика */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6 dark:bg-navy-700 dark:text-white dark:border-white/10">
        <div className="flex justify-between items-center">
          <div>
            <span className="text-sm text-gray-600">
              {currentLang === 'ru' ? 'Всего доноров:' : 'Total donors:'} {totalDonors}
            </span>
          </div>
          <div>
            <span className="text-sm text-gray-600">
              {currentLang === 'ru' ? 'Страница' : 'Page'} {currentPage} {currentLang === 'ru' ? 'из' : 'of'} {totalPages}
            </span>
          </div>
        </div>
      </div>

      {/* Ошибка */}
      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      {/* Таблица */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden dark:bg-navy-700 dark:text-white dark:border-white/10">
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 dark:bg-navy-700 dark:text-white dark:border-white/10">
                  <tr>
                    <th className="p-3 whitespace-nowrap text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      #
                    </th>
                    <th 
                      className="p-3 whitespace-nowrap text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:bg-navy-700 dark:text-white dark:border-white/10"
                      onClick={() => handleSort('url')}
                    >
                      {currentLang === 'ru' ? 'URL донора' : 'Donor URL'} {getSortIcon('url')}
                    </th>
                    <th 
                      className="p-3 whitespace-nowrap text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:bg-navy-700 dark:text-white dark:border-white/10"
                      onClick={() => handleSort('title')}
                    >
                      {currentLang === 'ru' ? 'Название' : 'Title'} {getSortIcon('title')}
                    </th>
                                          <th className="p-3 whitespace-nowrap text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:bg-navy-700 dark:text-white dark:border-white/10">
                        {currentLang === 'ru' ? 'Доступ' : 'Access'}
                      </th>
                    <th 
                      className="p-3 whitespace-nowrap text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:bg-navy-700 dark:text-white dark:border-white/10"
                      onClick={() => handleSort('created_at')}
                    >
                      {currentLang === 'ru' ? 'Дата' : 'Date'} {getSortIcon('created_at')}
                    </th>
                    <th className="p-3 whitespace-nowrap text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:bg-navy-700 dark:text-white dark:border-white/10">
                      {currentLang === 'ru' ? 'Действия' : 'Actions'}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200 dark:bg-navy-700 dark:text-white dark:border-white/10">
                  {donors.map((donor) => (
                    <tr key={donor.id} className="hover:bg-gray-50 dark:bg-navy-700 dark:text-white dark:border-white/10">
                      <td className="p-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleViewDonor(donor.id)}
                            className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-md dark:bg-navy-700 dark:text-white dark:border-white/10"
                            title={currentLang === 'ru' ? 'Просмотр' : 'View'}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </button>
                        </div>
                      </td>
                      <td className="p-3 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center space-x-2">
                        {donor.weight > 0 && (
                            <svg 
                              className="w-4 h-4 text-yellow-500 flex-shrink-0" 
                              fill="currentColor" 
                              viewBox="0 0 24 24"
                              title={currentLang === 'ru' ? 'Закреплено' : 'Pinned'}
                            >
                              <path d="M16 9V4h1V2H7v2h1v5c0 1.66-1.34 3-3 3v2h5.97v7l1 1l1-1v-7H19v-2c-1.66 0-3-1.34-3-3z"/>
                            </svg>
                          )}
                          <div className="max-w-xs flex-1">
                            <button
                              onClick={() => handleViewDonor(donor.id)}
                              className="text-blue-600 hover:text-blue-800 truncate block"
                              title={donor.url}
                            >
                              {truncateUrl(donor.url)}
                            </button>
                          </div>
                          
                        </div>
                      </td>
                                             <td className="p-3">
                         <div className="text-sm text-gray-900">{donor.title || '-'}</div>
                         <div className="text-sm text-gray-500">
                           <HtmlContent content={donor.description || ''} maxLength={100} />
                         </div>
                         <div className="text-sm text-blue-500">{donor.tags || ''}</div>
                       </td>
                       <td className="p-3 whitespace-nowrap text-sm text-gray-900">
                        {donor.access_users && donor.access_users.length > 0 ? (
                          <UserAvatars 
                            users={donor.access_users} 
                            maxVisible={3} 
                            size="sm" 
                          />
                        ) : (
                          <span className="text-gray-500">-</span>
                        )}
                      </td>
                      <td className="p-3 whitespace-nowrap text-sm text-gray-900 dark:bg-navy-700 dark:text-white dark:border-white/10">
                        <div>{formatDate(donor.created_at)}</div>
                      </td>
                      <td className="p-3 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex space-x-2">
                        {donor.access_users && donor.access_users.some(u => u.id === user?.id) && (
                            <button
                              onClick={() => handleEditDonor(donor.id)}
                              className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md"
                              title={currentLang === 'ru' ? 'Редактировать' : 'Edit'}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                          )}
                          {donor.access_users && donor.access_users.some(u => u.id === user?.id && u.type === 'owner') && (
                            <button
                              onClick={() => showDeleteConfirm(donor.id)}
                              disabled={deletingDonors.has(donor.id)}
                              className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md disabled:opacity-50"
                              title={currentLang === 'ru' ? 'Удалить' : 'Delete'}
                            >
                              {deletingDonors.has(donor.id) ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                              ) : (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Пагинация */}
            {totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    {currentLang === 'ru' ? 'Предыдущая' : 'Previous'}
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    {currentLang === 'ru' ? 'Следующая' : 'Next'}
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      {currentLang === 'ru' ? 'Показано' : 'Showing'} <span className="font-medium">{(currentPage - 1) * limit + 1}</span> {currentLang === 'ru' ? 'до' : 'to'} <span className="font-medium">{Math.min(currentPage * limit, totalDonors)}</span> {currentLang === 'ru' ? 'из' : 'of'} <span className="font-medium">{totalDonors}</span> {currentLang === 'ru' ? 'результатов' : 'results'}
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        ←
                      </button>
                      
                      {/* Номера страниц */}
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              currentPage === pageNum
                                ? 'z-10 bg-brand-50 border-brand-500 text-brand-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      
                      <button
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        →
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Сообщение об отсутствии данных */}
      {!loading && donors.length === 0 && !error && (
        <div className="text-center py-8 text-gray-500">
          {currentLang === 'ru' ? 'Доноры не найдены' : 'Donors not found'}
        </div>
      )}
      
      {/* Модальное окно создания донора */}
      <CreateDonorModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={handleCreateSuccess}
      />
      
      {/* Модальное окно редактирования донора */}
      <EditDonorModal
        isOpen={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedDonorId(null);
        }}
        donorId={selectedDonorId}
        onSuccess={handleEditSuccess}
        onUpdateDonor={updateDonorInList}
        onAccessChange={handleAccessChange}
      />
      
      {/* Модальное окно просмотра донора */}
      <ViewDonorModal
        isOpen={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false);
          setSelectedDonorId(null);
        }}
        donorId={selectedDonorId}
        onUpdateDonor={updateDonorInList}
      />

      {/* Модалка подтверждения удаления */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteConfirm}
        title={currentLang === 'ru' ? 'Подтверждение удаления' : 'Confirm Deletion'}
        message={currentLang === 'ru' 
          ? 'Вы уверены, что хотите удалить этого донора? Это действие нельзя отменить.' 
          : 'Are you sure you want to delete this donor? This action cannot be undone.'
        }
        confirmText={currentLang === 'ru' ? 'Удалить' : 'Delete'}
        cancelText={currentLang === 'ru' ? 'Отмена' : 'Cancel'}
        confirmButtonClass="bg-red-600 hover:bg-red-700"
        currentLang={currentLang}
      />
    </div>
  );
};

export default Donors; 